import { useCallback, useEffect, useMemo } from "react";

import { useLocalStorage } from "usehooks-ts";

import { createFNVHashSync } from "../authentication/utils";

const VISIBILITY_WINDOW_DAYS = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

interface AccountAnnouncementBannerState {
  firstSeenAt: string;
  dismissed: boolean;
}

export interface UseAccountAnnouncementBannerResult {
  visible: boolean;
  dismiss: () => void;
}

/**
 * Generate the localStorage key for a user's account-page announcement
 * banner state.
 *
 * @param userId - Unique user identifier
 * @returns localStorage key, formatted as `account-announcement-banner_{hashedUserId}`
 */
export const getAccountAnnouncementBannerStorageKey = (
  userId: string
): string => {
  const hashedId = createFNVHashSync(userId);
  return `account-announcement-banner_${hashedId}`;
};

/**
 * Tracks visibility of the account-page Transactions announcement banner.
 * `firstSeenAt` is written once, the first time `enabled` is true, and
 * never overwritten. Pass `enabled: false` while the banner isn't eligible
 * to render (e.g. the feature flag is off) so the 30-day clock doesn't
 * start before the user could have actually seen it.
 * The banner stays visible for 30 days from `firstSeenAt` unless dismissed.
 */
export const useAccountAnnouncementBanner = (
  userId: string,
  enabled: boolean
): UseAccountAnnouncementBannerResult => {
  const storageKey = useMemo(
    () => getAccountAnnouncementBannerStorageKey(userId),
    [userId]
  );
  const [state, setState] =
    useLocalStorage<AccountAnnouncementBannerState | null>(storageKey, null);

  useEffect(() => {
    if (enabled && state === null) {
      setState({ firstSeenAt: new Date().toISOString(), dismissed: false });
    }
  }, [enabled, state, setState]);

  const dismiss = useCallback(() => {
    setState((prev) => ({
      firstSeenAt: prev?.firstSeenAt ?? new Date().toISOString(),
      dismissed: true,
    }));
  }, [setState]);

  const visible = useMemo(() => {
    if (!enabled) return false;
    if (state === null) return true;
    if (state.dismissed) return false;
    const daysSinceFirstSeen =
      (Date.now() - new Date(state.firstSeenAt).getTime()) / MS_PER_DAY;
    return daysSinceFirstSeen < VISIBILITY_WINDOW_DAYS;
  }, [enabled, state]);

  return { visible, dismiss };
};
